import { type GridColDef } from '@mui/x-data-grid-premium';

import { type LedgerAccountListItem } from '~/data/ledgerAccount';

import i18n, { currentLocale } from '~/utils/i18n';

export type LedgerAccountRow = LedgerAccountListItem;

type AvailableColumn =
  | 'company'
  | keyof Pick<
      LedgerAccountRow,
      | 'accountType'
      | 'costType'
      | 'createdOn'
      | 'name'
      | 'number'
      | 'sourceType'
      | 'validFrom'
    >;

function getColumnHeader(column: AvailableColumn) {
  return i18n.t(($) => $.table.columns[column], { ns: 'ledgerAccounts' });
}

function getAccountTypeLabel(accountType: LedgerAccountRow['accountType']) {
  return i18n.t(($) => $.accountTypes[accountType], { ns: 'ledgerAccounts' });
}

function getSourceTypeLabel(sourceType: LedgerAccountRow['sourceType']) {
  return i18n.t(($) => $.sourceTypes[sourceType], { ns: 'ledgerAccounts' });
}

const dateFormatter = new Intl.DateTimeFormat(currentLocale(), {
  dateStyle: 'medium',
});

function toGridDate(value: string | undefined | null): Date | null {
  if (!value) {
    return null;
  }

  // Calendar dates (`YYYY-MM-DD`) must be parsed as local midnight. The
  // Date constructor treats a date-only ISO string as UTC midnight, which
  // can land on the previous local calendar day.
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00`)
    : new Date(value);

  if (!isFinite(date.getTime())) {
    return null;
  }

  return date;
}

function formatDate(value: Date | null | undefined) {
  if (!value || !isFinite(value.getTime())) {
    return '';
  }

  return dateFormatter.format(value);
}

// The API does not support server-side sorting on this list endpoint, so
// every column is unsortable rather than offering a control that silently
// does nothing.
export const columns: GridColDef<LedgerAccountRow>[] = [
  {
    field: 'number',
    headerName: getColumnHeader('number'),
    minWidth: 160,
    sortable: false,
  },
  {
    field: 'name',
    flex: 1,
    headerName: getColumnHeader('name'),
    minWidth: 200,
    sortable: false,
  },
  {
    field: 'company',
    flex: 1,
    headerName: getColumnHeader('company'),
    minWidth: 180,
    sortable: false,
    valueGetter: (_value, row) => row.company.name ?? '',
  },
  {
    field: 'accountType',
    headerName: getColumnHeader('accountType'),
    minWidth: 160,
    sortable: false,
    valueGetter: (value: LedgerAccountRow['accountType']) =>
      getAccountTypeLabel(value),
  },
  {
    field: 'costType',
    headerName: getColumnHeader('costType'),
    minWidth: 140,
    sortable: false,
    valueGetter: (value: LedgerAccountRow['costType']) => value ?? '',
  },
  {
    field: 'validFrom',
    headerName: getColumnHeader('validFrom'),
    minWidth: 120,
    sortable: false,
    type: 'date',
    valueFormatter: (value: Date | null | undefined) => formatDate(value),
    valueGetter: (value: string | undefined) => toGridDate(value),
  },
  {
    field: 'createdOn',
    headerName: getColumnHeader('createdOn'),
    minWidth: 120,
    sortable: false,
    type: 'date',
    valueFormatter: (value: Date | null | undefined) => formatDate(value),
    valueGetter: (value: string | undefined) => toGridDate(value),
  },
  {
    field: 'sourceType',
    headerName: getColumnHeader('sourceType'),
    minWidth: 120,
    sortable: false,
    valueGetter: (value: LedgerAccountRow['sourceType']) =>
      getSourceTypeLabel(value),
  },
];
